import PropTypes from 'prop-types';
import React, { createContext, useEffect, useMemo, useReducer } from 'react';

import { DisplayGridColumnPropType } from '../../prop-types/display-grid-column';
import { DisplayGridRecordPropType } from '../../prop-types/display-grid-record';
import { DisplayGridGroupRecordPropType } from '../../prop-types/display-grid-group-record';
import { GridHelperService } from '../../services/grid-helper-service';
import { getInitialState } from './reducer.initial-state';
import { setColumns, setLeftoverColumnWidth } from './actions';
import { DefaultPlaceholder } from './default-placeholder';
import { reducer } from './reducer';
import { emptyArray } from 'ui-components/default-props/empty';

/**
 * This is the primary engine behind the DisplayGrid. It contains a lot of
 * callback functions to be used in various other locations within this
 * component hierarchy. They are all memoized via the useCallback hook,
 * to have their reference updated only when their dependencies change,
 * and any downstream consumers that are dependent on them should handle
 * updates in the same way, listing these callbacks as their own dependencies.
 */

const Context = createContext();

const { Consumer, Provider } = Context;

const defaultGetColumnId = (column) => column.name;
const defaultPlaceholder = <DefaultPlaceholder />;

const GridContextProvider = React.forwardRef(
  (
    {
      children,
      columns: columnsProp,
      data,
      disableMultiSelect = false,
      renderCheckbox = true,
      getColumnId = defaultGetColumnId,
      height = null,
      onColumnsChanged,
      onOrderByChanged,
      onRowDoubleClick,
      shouldForceShowPlaceholder = false,
      placeholder = defaultPlaceholder,
      width = null,
      activeGroupByColumns = emptyArray,
      placeholderStyle = '',
    },
    ref,
  ) => {
    /**
     * When initializing a DisplayGrid, the 'columns' prop is the only one we allow
     * to override values in our initial reducer state. The state itself defaults
     * to an emptyArray, and whatever columns are passed in as props here will be
     * used on top of the getInitialState function to initialize the reducer state.
     */
    const initColumns = columnsProp === undefined ? getInitialState().columns : columnsProp;

    const [state, dispatch] = useReducer(reducer, {
      ...getInitialState(),
      columns: initColumns,
    });

    const { columns, isResizing, leftoverColumnWidth, resetToScrollTop, rowHovered } = state;

    /**
     * The useEffect hook here serves the purpose of the old-style class-based
     * callback for getDerivedStateFromProps. If we have a valid, non-null width,
     * we check for any width changes that might impact our rendered columns and
     * leftoverColumnWidth on the grid. We select the appropriate columns object
     * from either props or state, depending on whether we're currently resizing,
     * and update leftoverColumnWidth if we need to. In the end, if we're not in
     * the middle of a resizing operation, and the columns prop has been updated
     * so it no longer matches our state object, update our state object from it.
     */
    useEffect(() => {
      if (width) {
        const { pinnedColumns, totalWidth } = GridHelperService.getColumnsInfo(isResizing ? columns : columnsProp);
        const pinnedColumnsWidth = pinnedColumns.reduce(
          (acc, column) => acc + (column.width || column.minWidth),
          GridHelperService.getCheckboxColumnWidth(),
        );
        const nonPinnedColumnsWidth = totalWidth - pinnedColumnsWidth;
        let newLeftoverColumnWidth = 0;

        if (pinnedColumnsWidth + nonPinnedColumnsWidth < width) {
          newLeftoverColumnWidth = width - (pinnedColumnsWidth + nonPinnedColumnsWidth);
        }

        dispatch(setLeftoverColumnWidth(newLeftoverColumnWidth));
      }

      if (!isResizing && columnsProp !== columns) {
        dispatch(setColumns(columnsProp));
      }
    }, [columns, columnsProp, isResizing, width]);

    // Pull these values for caching on context, for convenience.
    const { nonPinnedColumns, pinnedColumns, totalWidth } = GridHelperService.getColumnsInfo(columns);
    const pinnedColumnsWidth = pinnedColumns.reduce(
      (acc, column) => acc + (column.width || column.minWidth),
      GridHelperService.getCheckboxColumnWidth(),
    );

    // Memoize the whole context value object to prevent unnecessary renders
    // downstream unless any of these values updates during a render cycle.
    const value = useMemo(
      () => ({
        columns,
        data,
        disableMultiSelect,
        dispatch,
        getColumnId,
        height,
        renderCheckbox,
        isResizing,
        leftoverColumnWidth,
        nonPinnedColumns,
        onColumnsChanged,
        resetToScrollTop,
        onOrderByChanged,
        onRowDoubleClick,
        pinnedColumns,
        pinnedColumnsWidth,
        placeholder,
        ref,
        rowHovered,
        totalWidth,
        width,
        activeGroupByColumns,
        shouldForceShowPlaceholder,
        placeholderStyle,
      }),
      [
        columns,
        data,
        disableMultiSelect,
        getColumnId,
        height,
        renderCheckbox,
        isResizing,
        leftoverColumnWidth,
        nonPinnedColumns,
        onColumnsChanged,
        resetToScrollTop,
        onOrderByChanged,
        onRowDoubleClick,
        pinnedColumns,
        pinnedColumnsWidth,
        placeholder,
        ref,
        rowHovered,
        totalWidth,
        width,
        activeGroupByColumns,
        shouldForceShowPlaceholder,
        placeholderStyle,
      ],
    );

    return <Provider value={value}>{children}</Provider>;
  },
);

GridContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  columns: PropTypes.arrayOf(DisplayGridColumnPropType).isRequired,
  data: PropTypes.arrayOf(PropTypes.oneOfType([DisplayGridRecordPropType, DisplayGridGroupRecordPropType])).isRequired,
  disableMultiSelect: PropTypes.bool,
  getColumnId: PropTypes.func,
  height: PropTypes.number,
  onColumnsChanged: PropTypes.func,
  onOrderByChanged: PropTypes.func,
  onRowDoubleClick: PropTypes.func,
  renderCheckbox: PropTypes.bool,
  placeholder: PropTypes.element,
  width: PropTypes.number,
  activeGroupByColumns: PropTypes.array,
};

const MemoizedGridContextProvider = React.memo(GridContextProvider);

export { Consumer, MemoizedGridContextProvider as Provider, Context };
